import { useMemo } from "react";

import IntegrationLogo from "@components/integration-logo/integration-logo";
import { IntegrationLogoType } from "@components/integration-logo/integration-logo-helpers";
import { pluralize } from "@helpers/string";

import {
  WorkHistoryExternalEventObject,
  WorkHistoryExternalObjectType,
} from "./helpers";

const externalObjectIcons: {
  [key in WorkHistoryExternalObjectType]: IntegrationLogoType;
} = {
  [WorkHistoryExternalObjectType.GITHUB_PULL_REQUEST]:
    IntegrationLogoType.Github,
  [WorkHistoryExternalObjectType.JIRA_ISSUE]: IntegrationLogoType.Jira,
  [WorkHistoryExternalObjectType.HUBSPOT_DEAL]: IntegrationLogoType.Hubspot,
  [WorkHistoryExternalObjectType.SALESFORCE_ACTIVITY]:
    IntegrationLogoType.Salesforce,
  [WorkHistoryExternalObjectType.LINEAR_ISSUE]: IntegrationLogoType.Linear,
  [WorkHistoryExternalObjectType.LINEAR_PROJECT]: IntegrationLogoType.Linear,
};

const ExternalEventObject = ({
  eventObject,
}: {
  eventObject: WorkHistoryExternalEventObject;
}) => {
  const iconType = externalObjectIcons[eventObject.type];

  const content = useMemo(() => {
    const link = (
      <a
        className="hover:underline font-medium"
        target="_blank"
        rel="noreferrer"
        href={eventObject.url}
      >
        {eventObject.title}
      </a>
    );
    if (eventObject.type === WorkHistoryExternalObjectType.HUBSPOT_DEAL) {
      const callCount = eventObject.engagements.filter(
        (engagement) => engagement.type === "call",
      ).length;
      const emailCount = eventObject.engagements.filter(
        (engagement) => engagement.type === "email",
      ).length;
      return (
        <>
          {link}
          <div className="text-xs text-gray-500 flex items-center gap-2">
            <div>{eventObject.stage}</div>
            {callCount > 0 && (
              <div className="italic">
                {`${callCount} ${pluralize("call", callCount)}`}
              </div>
            )}
            {emailCount > 0 && (
              <div className="italic">
                {`${emailCount} ${pluralize("email", emailCount)}`}
              </div>
            )}
          </div>
        </>
      );
    } else if (
      eventObject.type === WorkHistoryExternalObjectType.LINEAR_ISSUE
    ) {
      return (
        <>
          <span>Issue: </span>
          {link}
        </>
      );
    } else if (
      eventObject.type === WorkHistoryExternalObjectType.LINEAR_PROJECT
    ) {
      return (
        <>
          <span>Project ({eventObject.is_lead ? "lead" : "member"}): </span>
          {link}
        </>
      );
    } else {
      return link;
    }
  }, [eventObject]);

  return (
    <div className="flex items-center gap-2">
      <div>
        <IntegrationLogo type={iconType} />
      </div>
      <div>{content}</div>
    </div>
  );
};

export default ExternalEventObject;
